import { useEffect } from "react";
import {
  FieldError,
  FieldValues,
  useForm,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form";

import { Toast } from "@covertree/utils";

/**
 * Returns the first error message at any depth of RHF's nested `errors` tree (e.g. `units.0.unitLevelCoverages.<key>`).
 */
const findErrorMessage = (errors: unknown): string | undefined => {
  if (errors === null || typeof errors !== "object") return undefined;

  if (typeof (errors as FieldError).type === "string") {
    return (errors as FieldError).message;
  }

  for (const value of Object.values(errors)) {
    const message = findErrorMessage(value);
    if (message) return message;
  }

  return undefined;
};

export const useFormWithNotification = <
  TFieldValues extends FieldValues = FieldValues,
  TContext extends object = object,
>(
  props?: UseFormProps<TFieldValues, TContext>
): UseFormReturn<TFieldValues, TContext> => {
  const form = useForm<TFieldValues, TContext>(props);

  const {
    formState: { errors, submitCount },
  } = form;

  useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      Toast.showError(findErrorMessage(errors));
    }
  }, [errors, submitCount]);

  return form;
};
